import {
  type Event,
  type EventProcessor,
  type Integration,
  type ScopeContext,
  type SeverityLevel,
} from '@sentry/types';

import {
  Actionability,
  CapabilityTier,
  ContentType,
  CustomerImpact,
  EventClassification,
  highestPriorityTier,
} from '@canvas/operational-types';

import { getRank, rankingThresholdsToSeverity } from './error-ranking.ts';
import { type RankingDataTuple } from './ranking-key.ts';

export type SharedMetadataTags = {
  /**
   * Represents the capability tier associated with whatever you are logging.
   *
   * If whatever you're logging about is associated with more than one capability just pick the highest tier as we'll
   * want to prioritize the Sentry log based on its the highest tier capability it impacts.
   *
   * This is an Atlassian-wide SLO concept.
   * @see {@link https://developer.atlassian.com/platform/slos/tome/capabilities/#capability-tiers}
   */
  capabilityTier?: CapabilityTier;
  eventClassification?: EventClassification;
  customerImpact?: CustomerImpact;
  actionability?: Actionability;
  /**
   * Identifies which canvas content-type (whiteboard, slide, database, ...)
   * the event originated from. Lets dashboards/SLOs/detectors filter Sentry,
   * Splunk, and SignalFx by content type without inspecting URLs or payload
   * shape.
   *
   * Source: Slides Observability Gap Analysis (action item C1, P1).
   */
  contentType?: ContentType;
};

export type BugMetadata = {
  tags: SharedMetadataTags;
};

export type NonBugMetadata = {
  tags: SharedMetadataTags;
};

export type BugClassificationMetadata = BugMetadata | NonBugMetadata;

export const getSeverity = (data: RankingDataTuple): SeverityLevel => {
  const rank = getRank(data);
  for (const [threshold, severity] of rankingThresholdsToSeverity) {
    if (rank <= threshold) {
      return severity;
    }
  }

  /**
   * Thresholds are listed in declining levels of priority and therefore severity. As such, if we've fallen through every single
   * threshold, the event must ve a log
   */
  return 'log';
};

/**
 * Sentry doesn't support certain characters in tag anmes. This method sanitizes invalid characters in Sentry
 */
export const sanitizeTagName = (tagName: string) => tagName.replace(/[.//]/g, ':');

/**
 * A tagged template literal function. Prefix your `` strings to make sure the dynamic parts are sanitized
 * The static parts aren't sanitized. It's expected that you've sanitized this manually in the string literal itself
 */
export const tagName = (literals: TemplateStringsArray, ...dynamicStrings: string[]) => {
  let i = 0;
  const stringParts: string[] = [literals[0]];
  while (i < dynamicStrings.length) {
    stringParts[i * 2 + 1] = sanitizeTagName(dynamicStrings[i]);
    i++;
    stringParts[i * 2] = literals[i];
  }

  return stringParts.join('');
};

const uuidRegex = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
// Hexadecimal addresses/identifiers (e.g., 0x7ff8a1234567)
const hexAddressRegex = /\b0x[0-9a-fA-F]+\b/g;
// Hash-like strings (32+ character hex strings, common in Git hashes, content hashes, etc.)
const hashLikeRegex = /\b[0-9a-fA-F]{32,}\b/g;
// IPv4 addresses
const ipv4Regex = /\b(?:[0-9]{1,3}\.){3}[0-9]{1,3}\b/g;
/**
 * IPv6 addresses (handles full and compressed notation)
 *
 * @privateRemarks There are better regex expressions for IPv6 out there in the
 * wild such as {@link https://stackoverflow.com/a/17871737} but ultimately, we
 * don't really need a full proof regex. It'd be a waste of bytes in the bundle
 * and (probably) a waste of extra CPU compute.
 */
const ipv6Regex =
  /\b(?:[0-9a-fA-F]{0,4}:){2,7}[0-9a-fA-F]{0,4}\b|\b::[0-9a-fA-F:]*\b|\b[0-9a-fA-F:]*::\b/g;
// Large numbers that look like timestamps (10+ digits, but not years or common ranges)
// Apply this before other number sanitization to catch timestamps specifically
const unixTimestampRegex = /\b[0-9]{13}\b/g;
// Base64-like identifiers (long alphanumeric strings that could be session IDs, tokens, etc.)
const longIdentifierRegex = /\b[a-zA-Z0-9+/]{20,}={0,2}\b/g;
const decimalRegex = /-?(?<!(\.|\d))\d+\.\d+(?!(\.|\d))/g;

/**
 * The following try to sanitize 3 digit numbers as much as possible without sanitizing out HTTP statuses
 */
const anyPositiveWholeNumberThatsNot3Digits =
  /(?<=\s|^|'|"|\(|\[|\{)(\d|\d{2}|\d{4}\d*)(?='|"|\s|$|\)|\]|\})/g;
const notHttpStatus3DigitPositiveNumbers =
  /(?<=\s|^|'|"|\(|\[|\{)(6|7|8|9|0)(\d{2})(?='|"|\s|$|\)|\]|\})/g;
const threeDigitNegativeNumbers =
  /(?<=\s|^|'|"|\(|\[|\{)-(?:\d{3})(?='|"|\s|$|\)|\]|\})/g;

export const sanitizeMessage = (message: string) => {
  return (
    message
      .replace(uuidRegex, '<uuid>')
      .replace(hexAddressRegex, '<hex>')
      .replace(hashLikeRegex, '<hash>')
      .replace(ipv4Regex, '<ip>')
      .replace(ipv6Regex, '<ipv6>')
      .replace(longIdentifierRegex, '<identifier>')
      // Apply timestamp regex before general number sanitization
      .replace(unixTimestampRegex, '<timestamp>')
      .replace(decimalRegex, '<number>')
      .replace(threeDigitNegativeNumbers, '<number>')
      .replace(notHttpStatus3DigitPositiveNumbers, '<number>')
      .replace(anyPositiveWholeNumberThatsNot3Digits, '<number>')
      .replace(/\s+/, ' ')
  );
};

const textToSha256String = async (text: string) => {
  const textArrayBuffer = new TextEncoder().encode(text);

  // Text -> SHA-256 binary
  const hashBuffer = await crypto.subtle.digest('SHA-256', textArrayBuffer);

  // SHA-256 binary -> SHA-256 text
  // See: https://developer.mozilla.org/en-US/docs/Web/API/SubtleCrypto/digest
  return [...new Uint8Array(hashBuffer)]
    .map((x) => x.toString(16).padStart(2, '0'))
    .join('');
};

/**
 * Sanitizes message-like parts of fingerprints (E.g. The message itself and the transaction name) in a way that allows them to be grouped better by Sentry.
 * We'll only do this for events with custom fingerprints as Sentry does decent job of this already when no custom fingerprint is specified.
 */
const sanitizeMessageLikeFingerprints = async (
  event: Event,
): Promise<string[] | undefined> => {
  if (event.fingerprint === undefined) {
    return event.fingerprint;
  }

  const message = event.message;

  const newFingerprint = [...event.fingerprint];
  if (message !== undefined) {
    const messageIndex = newFingerprint.findIndex((item) => item === '{{ message }}');

    if (messageIndex !== -1) {
      const sanitizedMessage = sanitizeMessage(message);

      if (sanitizedMessage !== message) {
        const messageHash = await textToSha256String(sanitizedMessage);

        newFingerprint[messageIndex] = `message-${messageHash}`;
      }
    }
  }

  const transaction = event.transaction;

  if (transaction !== undefined) {
    const transactionIndex = newFingerprint.findIndex(
      (item) => item === '{{ transaction }}',
    );

    if (transactionIndex !== -1) {
      const sanitizedTransaction = sanitizeMessage(transaction);

      if (transaction !== sanitizedTransaction) {
        const transactionHash = await textToSha256String(sanitizedTransaction);

        newFingerprint[transactionIndex] = `transaction-${transactionHash}`;
      }
    }
  }

  return newFingerprint;
};

const FAILED_TO_FETCH_EVENT_MESSAGE = 'typeerror: failed to fetch';

export type CanvasSentryIntegrationOptions = {
  fallbackContentType?: ContentType;
};

/**
 * NOTE: This integration should always execute LAST in the integration list
 */
export class CanvasSentryIntegration implements Integration {
  name = 'canvas-sentry-integration';

  private readonly options: CanvasSentryIntegrationOptions;

  constructor(options: CanvasSentryIntegrationOptions = {}) {
    this.options = options;
  }

  setupOnce(addGlobalEventProcessor: (callback: EventProcessor) => void): void {
    addGlobalEventProcessor(async (event) => {
      const sanitizedFingerprints = await sanitizeMessageLikeFingerprints(event);

      const actionability =
        event.message?.toLowerCase() === FAILED_TO_FETCH_EVENT_MESSAGE
          ? Actionability.NONE
          : (event.tags?.actionability ?? TAG_FALLBACKS.actionability);

      const contentType =
        event.tags?.contentType === undefined ||
        event.tags.contentType === ContentType.UNKNOWN
          ? (this.options.fallbackContentType ?? TAG_FALLBACKS.contentType)
          : event.tags.contentType;

      return {
        ...event,
        tags: {
          ...TAG_FALLBACKS,
          ...event.tags,
          actionability,
          contentType,
        },
        ...(sanitizedFingerprints !== undefined
          ? { fingerprint: sanitizedFingerprints }
          : {}),
      };
    });
  }
}

export type CanvasCaptureExceptionOptions = Partial<ScopeContext> & {
  tags: {
    /**
     * Signifies the location in the codebase that the error was captured from.
     *
     * This makes identifying errors and grouping them simpler.
     *
     * Ideally, give the name something descriptive but ultimately this value simply just needs to be a
     * unique value so as to differentiate from other error locations.
     *
     * In the event of a collision, simply change one of the strings so that they are unique.
     *
     * This should be a STRING LITERAL. Do not make it dynamic. Making it dynamic makes it difficult to use the value to search for an error in the Canvas codebase.
     */
    captureLocation: string;
  };
  /**
   * Want this to log to console too?
   * Set this to true
   */
  consoleLog?: boolean;
} & BugClassificationMetadata;

export type CaptureExceptionForCapabilityOptions = CanvasCaptureExceptionOptions & {
  tags: Omit<CanvasCaptureExceptionOptions['tags'], 'capabilityTier'>;
};

export type CapabilityInfo = {
  capability: {
    importance: CapabilityTier;
    kebabCaseName: string;
  };
};

/**
 * A set of generic catch-all tag values for when there's no tag value specified for the given attribute
 */
export const TAG_FALLBACKS = {
  eventClassification: EventClassification.UNKNOWN,
  customerImpact: CustomerImpact.UNKNOWN,
  actionability: Actionability.UNKNOWN,
  capabilityTier: CapabilityTier.NOT_SPECIFIED,
  contentType: ContentType.UNKNOWN,
} satisfies Omit<CanvasCaptureExceptionOptions['tags'], 'captureLocation'>;

export const withCanvasEventMetadata = (
  options: CanvasCaptureExceptionOptions,
): Partial<ScopeContext> => {
  const eventClassification =
    options.tags.eventClassification ?? TAG_FALLBACKS.eventClassification;
  const customerImpact = options.tags.customerImpact ?? TAG_FALLBACKS.customerImpact;
  const actionability = options.tags.actionability ?? TAG_FALLBACKS.actionability;
  const capabilityTier = options.tags.capabilityTier ?? TAG_FALLBACKS.capabilityTier;
  const contentType = options.tags.contentType ?? TAG_FALLBACKS.contentType;

  /**
   * We change the severity as a way of making it more obvious which errors need to be investigated first
   */
  const level = getSeverity([
    eventClassification,
    customerImpact,
    capabilityTier,
    actionability,
  ]);

  return {
    level,
    /**
     * This changes how Sentry groups are created for anything that uses this config.
     *
     * @see {@link https://docs.sentry.io/concepts/data-management/event-grouping/fingerprint-rules/} for information about fingerprinting rules
     *
     * @see {@link https://atlassian-2y.sentry.io/settings/projects/<project-name>/issue-grouping/} which contains some Sentry server side rules that
     * we might also have declared.
     */
    fingerprint: [
      /**
       * We use this in place of the stacktrace to avoid the chance of Sentry creating a new group on each new deployment.
       * @see {@link CanvasCaptureExceptionOptions}
       */
      '{{ tags.captureLocation }}',

      // We don't want on-call to have to filter through a bunch of low severity errors inside a group with high severity errors in there as well
      '{{ level }}',

      '{{ message }}',

      '{{ tags.actionability }}',
    ],
    ...options,
    tags: {
      ...options.tags,
      eventClassification,
      customerImpact,
      actionability,
      capabilityTier,
      contentType,
    },
  };
};

export const withCapabilityMetadata = (
  options: CaptureExceptionForCapabilityOptions,
  capabilityInfoListOrCapabilityInfo: CapabilityInfo[] | CapabilityInfo,
) => {
  const capabilityInfoList = Array.isArray(capabilityInfoListOrCapabilityInfo)
    ? capabilityInfoListOrCapabilityInfo
    : [capabilityInfoListOrCapabilityInfo];

  return {
    ...options,
    tags: {
      ...options.tags,
      capabilityTier: highestPriorityTier(
        capabilityInfoList.map((c) => c.capability.importance),
      ),
      capabilityName: capabilityInfoList[0].capability.kebabCaseName,
    },
  } as CanvasCaptureExceptionOptions;
};
